import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {validationStore} from "../ValidationStore";
import {AcquisitionDataDto, AcquisitionDocumentDto, AcquisitionStatusDto} from "../../api/sca";
import {equals} from "../../utils";
import {ScaFormData, ScaFormValidation} from "./commons";
import {ScaCustomerItem} from "./ScaCustomerStore";

const systemApi = api.sca.api

class ScaCreateStore {
    private get customer(): ScaCustomerItem | undefined {
        return this._customer;
    }

    private set customer(value: ScaCustomerItem | undefined) {
        this._customer = value;
    }

    get initial(): AcquisitionDataDto {
        return this._initial;
    }

    set initial(value: AcquisitionDataDto) {
        this._initial = value;
    }

    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }

    get externalReference(): string {
        return this._externalReference;
    }

    set externalReference(value: string) {
        this._externalReference = value;
    }

    get documents(): AcquisitionDocumentDto[] {
        return this._documents;
    }

    set documents(value: AcquisitionDocumentDto[]) {
        this._documents = value;
    }

    get customerId(): string {
        return this._customerId;
    }

    set customerId(value: string) {
        this._customerId = value;
    }

    get correlationId(): string {
        return this._correlationId;
    }

    set correlationId(value: string) {
        this._correlationId = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    private set valid(value: boolean) {
        this._valid = value;
    }

    get creating(): boolean {
        return this._creating;
    }

    private set creating(value: boolean) {
        this._creating = value
    }

    get dirty(): boolean {
        return !equals(this.buildDto(), this.initial);
    }

    get formDisabled(): boolean {
        return false // TODO
    }

    get formData(): ScaFormData {
        const {
            customer,
            vin,
            externalReference,
            documents
        } = this
        const docs = documents.map(e => ({
            type: e.type
        }))
        return {
            vin,
            externalReference,
            customer,
            documents: docs
        }
    }

    set formData(v: ScaFormData) {
        const {
            customer,
            vin,
            externalReference,
            documents,
        } = v
        this.customer = customer
        this.customerId = customer?.id || ''
        this.vin = vin
        this.externalReference = externalReference
        this.documents = documents
    }

    get formValidation(): ScaFormValidation {
        return {
            customer: validationStore.getFor('customerId'),
            vin: validationStore.getFor('vin'),
            externalReference: validationStore.getFor('externalReference'),
            documents: validationStore.getFor('documents'),
        }
    }

    private _correlationId: string = ''
    private _customerId: string = ''
    private _documents: AcquisitionDocumentDto[] = []
    private _externalReference: string = ''
    private _vin: string = ''

    private _creating: boolean = false
    private _valid: boolean = false
    private _initial: AcquisitionDataDto = this.buildDto()
    private _customer: ScaCustomerItem | undefined

    constructor() {
        makeAutoObservable(this)
    }

    reset() {
        this.vin = ''
        this.documents = []
        this.customerId = ''
        this.customer = undefined
        this.correlationId = crypto.randomUUID()
        this.externalReference = ''
        this.initial = this.buildDto()
    }

    async create(): Promise<string | undefined> {
        const dto = this.buildDto()
        this.creating = true
        const response = await catchApi(async () => await systemApi.createSecurityCodeAcquisition(dto))
        this.creating = false

        return response?.data.id
    }

    async validate() {
        const dto = this.buildDto()

        await validationStore.validate(systemApi.validateSecurityCodeAcquisition, dto)

        this.valid = Boolean(validationStore.valid)
    }

    private buildDto(): AcquisitionDataDto {
        const status = AcquisitionStatusDto.INITIALIZED
        const {
            vin,
            documents,
            customerId,
            correlationId,
            externalReference
        } = this
        return {
            vin,
            documents,
            customerId,
            correlationId,
            externalReference,
            status
        }
    }
}

const scaCreateStore = new ScaCreateStore()

export {
    scaCreateStore
}
